import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInUser, resetPassword } from '../../firebase/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Footer from '../Footer';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData } = useAuth();
  const fromState = location.state?.from;
  const from = typeof fromState === 'string'
    ? fromState
    : fromState
      ? (fromState.pathname || '') + (fromState.hash || '')
      : '/dashboard';
  const { t } = useLanguage();
  const loginCopy = t('auth.login', {});
  const resetCopy = t('auth.reset', {});
  const authErrors = t('auth.messages', {});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await signInUser(data.email, data.password);

      if (result.success) {
        setUserData(result.userData);
        navigate(from, { replace: true });
      } else {
        setError(authErrors.invalidCredentials);
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found') {
        setError(authErrors.userNotFound);
      } else if (err.code === 'auth/wrong-password') {
        setError(authErrors.wrongPassword);
      } else if (err.code === 'auth/invalid-email') {
        setError(authErrors.invalidEmail);
      } else {
        setError(authErrors.generic);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (data) => {
    setIsLoading(true);
    setResetMessage('');

    try {
      await resetPassword(data.email);
      setResetMessage(authErrors.resetSuccess);
    } catch (err) {
      console.error('Password reset error:', err);
      setResetMessage(authErrors.resetError);
    } finally {
      setIsLoading(false);
    }
  };

  if (showResetPassword) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-prayer-red via-purple-600 to-blue-600 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              {resetCopy.title}
            </h2>
              <p className="text-white/80 mb-8">
                {resetCopy.subtitle}
              </p>
            </div>

            <form onSubmit={handleSubmit(handlePasswordReset)} className="bg-white rounded-2xl shadow-2xl p-8 space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {loginCopy.fields?.email?.label}
                </label>
                <input
                  {...register('email', { 
                    required: t('validation.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('validation.emailInvalid')
                    }
                  })}
                  type="email"
                  id="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
                  placeholder={loginCopy.fields?.email?.placeholder}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {resetMessage && (
                <div className={`p-4 rounded-lg ${
                  resetMessage.includes('Error') 
                    ? 'bg-red-50 border border-red-200 text-red-700' 
                    : 'bg-green-50 border border-green-200 text-green-700'
                }`}>
                  <p className="text-sm">{resetMessage}</p>
                </div>
              )}

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-prayer-red text-white py-3 px-6 rounded-lg font-medium hover:bg-prayer-red-light transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? t('common.loading') : resetCopy.submit}
                </button>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="flex-1 bg-white/20 text-white py-3 px-6 rounded-lg font-medium hover:bg-white/30 transition-all duration-200"
                  >
                    {loginCopy.backToHome}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(false)}
                    className="flex-1 bg-white/20 text-white py-3 px-6 rounded-lg font-medium hover:bg-white/30 transition-all duration-200"
                  >
                    {resetCopy.backToLogin}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-prayer-red via-purple-600 to-blue-600 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              {loginCopy.title}
            </h2>
            <p className="text-white/80 mb-8">
              {loginCopy.subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-2xl shadow-2xl p-8 space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {loginCopy.fields?.email?.label}
              </label>
              <input
                {...register('email', { 
                  required: t('validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('validation.emailInvalid')
                  }
                })}
                type="email"
                id="email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
                placeholder={loginCopy.fields?.email?.placeholder}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {loginCopy.fields?.password?.label}
              </label>
              <input
                  {...register('password', { required: t('validation.passwordRequired') })}
                type="password"
                id="password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-prayer-red focus:border-transparent transition-all duration-200"
                  placeholder={loginCopy.fields?.password?.placeholder}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="ml-3 text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowResetPassword(true)}
                className="text-sm text-prayer-red hover:text-prayer-red-light font-medium"
              >
                {loginCopy.forgotPassword}
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-prayer-red text-white py-3 px-6 rounded-lg font-medium hover:bg-prayer-red-light transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{loginCopy.loading}</span>
                </>
              ) : (
                <span>{loginCopy.submit}</span>
              )}
            </button>

            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">
                {loginCopy.noAccount}{' '}
                  <Link to="/auth/register" className="text-prayer-red hover:text-prayer-red-light font-medium">
                  {loginCopy.createAccount}
                  </Link>
                </p>
              </div>
              
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full bg-white text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200 border border-gray-200"
              >
                {loginCopy.backToHome}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Login;
